import { useState, useEffect } from "react";
import "./product.css";
import "./CRM.css"; // Reuse common styles
import { useNavigate } from "react-router-dom";
import dataService from "../services/dataService";
import authService from "../services/authService";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { Eye, FileEdit, Trash2, Search, Plus, Package, Download } from "lucide-react";

function ProductScreen() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [formData, setFormData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await dataService.getProducts();
      setProducts(response.data.data);
    } catch (error) {
      console.error("Failed to fetch products", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleAddProduct = () => {
    navigate("/products/add");
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      
      const headers = rows[0].map(h => h.trim().replace(/"/g, ''));
      const nameIdx = headers.findIndex(h => h.toLowerCase().includes('name'));
      const skuIdx = headers.findIndex(h => h.toLowerCase().includes('sku'));
      const categoryIdx = headers.findIndex(h => h.toLowerCase().includes('category'));
      const descIdx = headers.findIndex(h => h.toLowerCase().includes('description') || h.toLowerCase().includes('desc'));
      const stockIdx = headers.findIndex(h => h.toLowerCase().includes('stock') || h.toLowerCase().includes('qty') || h.toLowerCase().includes('quantity'));
      const priceIdx = headers.findIndex(h => h.toLowerCase().includes('retail') || h.toLowerCase().includes('price') || h.toLowerCase().includes('rate'));
      const costIdx = headers.findIndex(h => h.toLowerCase().includes('cost'));
      const warehouseIdx = headers.findIndex(h => h.toLowerCase().includes('warehouse') || h.toLowerCase().includes('location'));

      const newProducts = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2) continue;
        
        const stockVal = parseInt((row[stockIdx] || '0').replace(/"/g, '').trim(), 10) || 0;
        const retailVal = parseFloat((row[priceIdx] || '0').replace(/"/g, '').trim()) || 0;
        const costVal = parseFloat((row[costIdx] || '0').replace(/"/g, '').trim()) || 0;

        newProducts.push({
          name: (row[nameIdx] || 'Imported Product').replace(/"/g, '').trim(),
          sku: (row[skuIdx] || `SKU-${Date.now()}-${i}`).replace(/"/g, '').trim(),
          category: (row[categoryIdx] || 'technology').replace(/"/g, '').trim().toLowerCase(),
          description: (row[descIdx] || '').replace(/"/g, '').trim(),
          stockCount: stockVal,
          retailPrice: retailVal,
          wholesalePrice: retailVal,
          costPrice: costVal || (retailVal * 0.6),
          stockTarget: stockVal > 10 ? stockVal : 10,
          warehouseLocation: (row[warehouseIdx] || 'Main Warehouse - dubai').replace(/"/g, '').trim(),
          status: stockVal > 10 ? 'In Stock' : stockVal > 0 ? 'Low Stock' : 'Out of Stock'
        });
      }

      if (newProducts.length > 0) {
        addToast(`Importing ${newProducts.length} products...`, "success");
        try {
          for (const prod of newProducts) {
            await dataService.addProduct(prod);
          }
          fetchProducts();
          addToast("Import successful!", "success");
        } catch (err) {
          addToast("Error during import", "error");
        }
      }
    };
    reader.readAsText(file);
  };

  const handleView = (product) => {
    setSelectedProduct(product);
    setModalMode("view");
    setIsModalOpen(true);
  };

  const handleEdit = (product) => {
    navigate(`/products/edit/${product._id}`);
  };

  const handleDeleteClick = (product) => {
    setItemToDelete(product);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dataService.deleteProduct(itemToDelete._id);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchProducts();
      addToast("Product deleted successfully", "success");
    } catch (error) {
      addToast("Failed to delete product: " + error.message, "error");
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      };

      const payload = { ...formData };
      delete payload.variations;

      if (imageFile) {
        payload.imageUrl = await convertToBase64(imageFile);
      }

      await dataService.updateProduct(selectedProduct._id, payload);
      setIsModalOpen(false);
      fetchProducts();
      addToast("Product updated successfully", "success");
    } catch (error) {
      addToast("Failed to update product: " + error.message, "error");
    }
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Product Catalog</h1>
          <p>Inventory management and stock tracking</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="secondary-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Import CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          {authService.hasPermission('product', 'editProduct') && (
            <button className="primary-btn" onClick={handleAddProduct}>
              <Plus size={18} /> Add New Product
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Items</span>
            <h2 className="stat-value">{products.length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Inventory Value</span>
            <h2 className="stat-value">AED {products.reduce((acc, p) => acc + (p.retailPrice * p.stockCount), 0).toLocaleString()}</h2>
          </div>
        </div>
      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <h2 className="font-bold">Products</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search products by name, SKU, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Loading products...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product Details</th>
                  <th>Category</th>
                  <th>Stock Count</th>
                  <th>Pricing</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products
                  .filter(p =>
                    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    p.sku.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((product) => (
                    <tr key={product._id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {product.imageUrl ? (
                            <img
                              src={dataService.getImageUrl(product.imageUrl)}
                              alt={product.name}
                              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          ) : (
                            <div style={{ width: '40px', height: '40px', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                              <Package size={20} color="#9CA3AF" />
                            </div>
                          )}
                          <div>
                            <div className="font-bold">{product.name}</div>
                            <div style={{ fontSize: '12px', color: '#6B7280' }}>SKU: {product.sku}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="category-pill">{product.category}</span></td>
                      <td>
                        <div className="font-bold">{product.stockCount}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Target: {product.stockTarget}</div>
                      </td>
                      <td>
                        <div className="font-bold">R: AED {product.retailPrice}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>W: AED {product.wholesalePrice}</div>
                      </td>
                      <td>
                        <span className={`status-pill ${product.status.replace(/ +/g, '-').toLowerCase()}`}>
                          {product.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="action-icons">
                          <button className="icon-btn" title="View" onClick={() => handleView(product)}>
                            <Eye size={16} />
                          </button>

                          {authService.hasPermission('product', 'editProduct') && (
                            <button className="icon-btn success" title="Edit" onClick={() => handleEdit(product)}>
                              <FileEdit size={16} />
                            </button>
                          )}

                          {authService.hasPermission('product', 'deleteProduct') && (
                            <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteClick(product)}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {products.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No products found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === "view" ? "Product Details" : "Edit Product"}
        footer={
          modalMode === "edit" ? (
            <>
              <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="primary-btn" onClick={handleUpdateSubmit}>Save Changes</button>
            </>
          ) : (
            <button className="primary-btn" onClick={() => setIsModalOpen(false)}>Close</button>
          )
        }
      >
        {modalMode === "view" && selectedProduct && (
          <div className="detail-view">
            <div className="detail-item">
              <span className="detail-label">Product Name</span>
              <span className="detail-value">{selectedProduct.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">SKU</span>
              <span className="detail-value">{selectedProduct.sku}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Category</span>
              <span className="category-pill">{selectedProduct.category}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Description</span>
              <span className="detail-value">{selectedProduct.description}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Stock Count</span>
              <span className="detail-value">{selectedProduct.stockCount} (Target: {selectedProduct.stockTarget})</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Retail Price</span>
              <span className="detail-value">AED {selectedProduct.retailPrice}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Wholesale Price</span>
              <span className="detail-value">AED {selectedProduct.wholesalePrice}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Supplier</span>
              <span className="detail-value">{selectedProduct.supplier}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Warehouse Location</span>
              <span className="detail-value">{selectedProduct.warehouseLocation || "Not specified"}</span>
            </div>
            <div className="detail-item">
              <span className="selected-image-label">Product Image</span>
              <div className="detail-value">
                {selectedProduct.imageUrl ? (
                  <img
                    src={dataService.getImageUrl(selectedProduct.imageUrl)}
                    alt={selectedProduct.name}
                    style={{ width: '100%', maxWidth: '200px', borderRadius: '8px', marginTop: '8px' }}
                  />
                ) : (
                  <p style={{ color: '#6B7280', fontStyle: 'italic' }}>No image uploaded</p>
                )}
              </div>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-pill ${selectedProduct.status?.replace(/ +/g, '-').toLowerCase()}`}>
                {selectedProduct.status}
              </span>
            </div>
          </div>
        )}

        {modalMode === "edit" && (
          <form className="modal-form" onSubmit={handleUpdateSubmit}>
            <div className="form-group">
              <label>Product Name</label>
              <input name="name" value={formData.name || ""} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>SKU</label>
              <input name="sku" value={formData.sku || ""} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select name="category" value={formData.category || ""} onChange={handleFormChange}>
                <option value="technology">technology</option>
                <option value="drinkware">drinkware</option>
                <option value="apparel">apparel</option>
                <option value="trophies">trophies</option>
                <option value="stationary">stationary</option>
                <option value="badges">badges</option>
                <option value="bags">bags</option>
                <option value="gift boxes">gift boxes</option>
                <option value="wrist bands">wrist bands</option>
                <option value="lanyards">lanyards</option>
                <option value="eco friendly">eco friendly</option>
                <option value="soft toys">soft toys</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea name="description" value={formData.description || ""} onChange={handleFormChange}></textarea>
            </div>
            <div className="form-group">
              <label>Stock Count</label>
              <input type="number" name="stockCount" value={formData.stockCount || 0} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Retail Price</label>
              <input type="number" name="retailPrice" value={formData.retailPrice || 0} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Warehouse Location</label>
              <select name="warehouseLocation" value={formData.warehouseLocation || "Main Warehouse - dubai"} onChange={handleFormChange}>
                <option value="Main Warehouse - dubai">Main Warehouse - dubai</option>
                <option value="West Coast Hub - sharjah">West Coast Hub - sharjah</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={formData.status || "In Stock"} onChange={handleFormChange}>
                <option value="In Stock">In Stock</option>
                <option value="Low Stock">Low Stock</option>
                <option value="Out of Stock">Out of Stock</option>
              </select>
            </div>
            <div className="form-group">
              <label>Product Image</label>
              <input type="file" accept="image/*" onChange={handleFileChange} />

              {/* Show new image preview if selected, otherwise show current image thumbnail */}
              {imageFile ? (
                <div style={{ marginTop: '10px' }}>
                  <img
                    src={URL.createObjectURL(imageFile)}
                    alt="New Preview"
                    style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                  />
                  <p style={{ fontSize: '11px', color: '#059669', marginTop: '4px' }}>New image selected</p>
                </div>
              ) : selectedProduct?.imageUrl && (
                <div style={{ marginTop: '10px' }}>
                  <img
                    src={dataService.getImageUrl(selectedProduct.imageUrl)}
                    alt="Current"
                    style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                  />
                  <p style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>Current image</p>
                </div>
              )}
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            <button className="primary-btn btn-danger" onClick={confirmDelete}>Delete Product</button>
          </>
        }
      >
        <div className="delete-confirm-view">
          <div className="delete-icon-wrapper">
            <Trash2 size={32} />
          </div>
          <h3>Are you sure?</h3>
          <p>You are about to delete <strong>{itemToDelete?.name}</strong>. This action cannot be undone and will remove the product from the catalog.</p>
        </div>
      </Modal>
    </div>
  );
}

export default ProductScreen;
